import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, MapPin, FileText, UserRound } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT, inr } from "@/lib/brand";
import { PriceRows, RequireAuth, StatusBadge } from "@/components/site/Shared";
import { openCashfree } from "@/pages/Checkout";
import { LiveLocationShare } from "@/components/trip/LiveLocationShare";
import { ReviewBox } from "@/components/site/ReviewBox";
import { ReferCard } from "@/components/site/ReferCard";
import { PushOptIn } from "@/components/site/PushOptIn";
import { ConfirmEmail } from "@/components/site/ConfirmEmail";
import { TripInspection } from "@/components/trip/TripInspection";
import { ChangeButtons } from "@/components/trip/ChangeBooking";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function BookingCard({ b, onChange, review }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const cancel = async () => {
    try {
      const r = await api.post(`/bookings/${b.id}/cancel`);
      toast.success(r.data.refund_amount != null ? `Cancelled. Refund of ${inr(r.data.refund_amount)} will be processed.` : "Booking cancelled");
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const payNow = async () => {
    try {
      const r = await api.post(`/bookings/${b.id}/pay`);
      await openCashfree(r.data.payment_session_id, r.data.mode);
    } catch (e) { toast.error(errMsg(e)); onChange(); }
  };
  return (
    <div className="fade-up overflow-hidden rounded-3xl border border-black/5 bg-white shadow-sm" data-testid={`booking-card-${b.id}`}>
      <div className="grid gap-5 p-5 sm:grid-cols-[180px_1fr_auto] sm:items-center">
        <img src={b.car_image} alt={b.car_name} className="aspect-[3/2] w-full rounded-2xl object-cover" />
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-lg font-semibold">{b.car_name}</h3>
            <StatusBadge status={b.status} testId={`booking-status-${b.id}`} />
          </div>
          <p className="text-xs text-neutral-500">Booking ID {b.id}</p>
          <p className="flex items-center gap-2 text-sm text-neutral-700"><CalendarClock className="h-4 w-4 text-[#FF5A00]" />{fmtDT(b.pickup_at)} → {fmtDT(b.return_at)}</p>
          <p className="flex items-center gap-2 text-sm text-neutral-700"><MapPin className="h-4 w-4 text-[#FF5A00]" />{b.pickup_location}</p>
          {b.with_driver && (
            <p className="flex items-center gap-2 text-sm text-neutral-700" data-testid={`booking-driver-${b.id}`}>
              <UserRound className="h-4 w-4 text-[#FF5A00]" />
              {b.driver ? <>Driver: <span className="font-semibold">{b.driver.name}</span> · <a href={`tel:${b.driver.phone}`} className="text-[#FF5A00]">{b.driver.phone}</a></> : "With Driver · driver will be assigned before pickup"}
            </p>
          )}
          {b.refund_status && <p className="text-sm text-emerald-700" data-testid={`booking-refund-${b.id}`}>Refund {inr(b.refund_amount)} · {b.refund_status.toLowerCase()}</p>}
          {(b.extensions?.length > 0 || b.reschedule_count > 0) && <p className="text-xs text-hc-slate" data-testid={`booking-changes-${b.id}`}>{b.extensions?.length ? `Extended ${b.extensions.length}×` : ""}{b.extensions?.length && b.reschedule_count ? " · " : ""}{b.reschedule_count ? "Rescheduled" : ""}</p>}
        </div>
        <div className="flex flex-row flex-wrap gap-2 sm:flex-col sm:items-end">
          <p className="font-display text-xl font-bold">{inr(b.quote.total)}</p>
          {b.status === "PENDING_PAYMENT" && <button onClick={payNow} data-testid={`booking-pay-${b.id}`} className="rounded-full bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white">Pay now</button>}
          {b.payment_status === "PAID" && <Link to={`/invoice/${b.id}`} data-testid={`booking-invoice-${b.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 text-sm font-semibold transition-colors duration-200 hover:border-[#FF5A00] hover:text-[#FF5A00]"><FileText className="h-4 w-4" />Invoice</Link>}
          {b.payment_status === "PAID" && <ChangeButtons b={b} onDone={onChange} />}
          {["PENDING_PAYMENT", "CONFIRMED"].includes(b.status) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid={`booking-cancel-${b.id}`} className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold transition-colors duration-200 hover:border-rose-400 hover:text-rose-600">Cancel</button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Full refund of rental charges 48+ hours before pickup, 50% between 24–48 hours, none within 24 hours. Your security deposit is always refunded in full.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-dialog-keep">Keep booking</AlertDialogCancel>
                  <AlertDialogAction onClick={cancel} data-testid="cancel-dialog-confirm" className="bg-rose-600 hover:bg-rose-700">Yes, cancel</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <button onClick={() => setOpen(!open)} data-testid={`booking-details-${b.id}`} className="text-sm font-semibold text-[#FF5A00]">{open ? "Hide" : "Details"}</button>
        </div>
      </div>
      {b.status === "ACTIVE" && <div className="border-t border-black/5 p-5"><LiveLocationShare bookingId={b.id} /></div>}
      {b.status === "COMPLETED" && <div className="border-t border-black/5 bg-orange-50/40 p-5"><ReviewBox booking={b} existing={review} onDone={onChange} /></div>}
      {open && <div className="space-y-5 border-t border-black/5 bg-neutral-50 p-5"><PriceRows q={b.quote} /><TripInspection b={b} /></div>}
    </div>
  );
}

function BookingsInner() {
  const api = useApi();
  const [list, setList] = useState(null);
  const [reviews, setReviews] = useState({});
  const load = useCallback(() => {
    api.get("/bookings").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/my/reviews").then((r) => setReviews(Object.fromEntries(r.data.map((x) => [x.booking_id, x])))).catch(() => {});
  }, [api]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="container-x max-w-5xl py-12" data-testid="my-bookings-page">
      <p className="label-eyebrow">Your trips</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">My bookings</h1>
      <div className="mt-10 space-y-5">
        <ConfirmEmail />
        <PushOptIn />
        <ReferCard />
        {list === null && <p className="text-neutral-500">Loading…</p>}
        {list?.length === 0 && (
          <div className="rounded-3xl border border-dashed border-black/15 bg-white p-12 text-center" data-testid="bookings-empty">
            <p className="text-neutral-600">No bookings yet.</p>
            <Link to="/cars" className="mt-4 inline-block rounded-full bg-[#FF5A00] px-6 py-2.5 font-semibold text-white">Find a car</Link>
          </div>
        )}
        {list?.map((b) => <BookingCard key={b.id} b={b} onChange={load} review={reviews[b.id]} />)}
      </div>
    </div>
  );
}

export default function MyBookings() {
  return <RequireAuth><BookingsInner /></RequireAuth>;
}
